<template>
  <div v-if="visible" class="extension-prompt" role="region" :aria-label="title">
    <div class="prompt-content">
      <div class="prompt-icon" aria-hidden="true"><i class="pi pi-bolt"></i></div>
      <div class="prompt-text">
        <h4>{{ title }}</h4>
        <p>{{ description }}</p>
      </div>
      <div class="prompt-actions">
        <a :href="ctaUrl" target="_blank" rel="noopener noreferrer" class="prompt-cta" @click="onInstallClick">
          {{ ctaLabel }}
        </a>
        <Button
          icon="pi pi-times"
          :aria-label="$t('extensionPrompt.dismiss')"
          class="p-button-text p-button-sm"
          @click="dismiss"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import Button from 'primevue/button'
import { useI18n } from 'petite-vue-i18n'
import { useExtensionInstalled } from '../composables/useExtensionInstalled.js'
import { detectBrowser, storeUrlFor, isExtensionCapable, hasExtensionFor, bookmarkletUrlFor } from '../utils/extensionStores.js'

const DISMISSED_KEY = 'linkflare_extension_prompt_dismissed'
// Its own key, so dismissing the bookmarklet pointer does not also hide the
// real Safari extension offer once that is live.
const BOOKMARKLET_DISMISSED_KEY = 'linkflare_safari_bookmarklet_prompt_dismissed'

/**
 * Persistent (but dismissable) nudge to install the browser extension.
 *
 * The extension owns the new tab page — review queue, rediscover, inbox — and
 * is the only one-click way to save, which makes it the strongest repeat-use
 * driver in the product. Before this it was offered exactly once, as a
 * skippable onboarding step, and never mentioned again.
 *
 * Hides itself permanently once the extension is detected, and stays hidden
 * for good if the user dismisses it.
 *
 * A browser with no Linkflare extension (Safari, see SAFARI_EXTENSION_LIVE)
 * gets the bookmarklet and Add to Dock pointer instead of a store link.
 */
export default {
  name: 'ExtensionPrompt',
  components: { Button },
  setup() {
    const { t, locale } = useI18n()
    // The one owner of the extension_install milestone: this component is
    // mounted for the whole authenticated session, so every other caller of the
    // composable can stay a pure read.
    const { extensionInstalled } = useExtensionInstalled({ track: true })

    const browser = detectBrowser()
    // No Linkflare extension for this browser (Safari while SAFARI_EXTENSION_LIVE
    // is off): point to the bookmarklet and Add to Dock instead.
    const bookmarkletOnly = !hasExtensionFor(browser)
    const dismissedKey = bookmarkletOnly ? BOOKMARKLET_DISMISSED_KEY : DISMISSED_KEY

    const dismissed = ref((() => {
      try {
        return localStorage.getItem(dismissedKey) === '1'
      } catch (e) {
        return false
      }
    })())

    // The Mac wrapper enables its Safari extension through system settings,
    // not a store page, so a store link would be a dead end there.
    const isMacApp = typeof window !== 'undefined' && window.__LINKFLARE_MAC_APP === true

    // Phones and tablets cannot install a desktop extension, so the store link
    // there is a dead end — don't nag.
    const canInstall = isExtensionCapable()
    // Already running as a Dock web app: telling them to Add to Dock is noise.
    const runsFromDock = typeof window !== 'undefined' &&
      window.matchMedia?.('(display-mode: standalone)')?.matches === true

    const title = computed(() =>
      bookmarkletOnly ? t('extensionPrompt.safariTitle') : t('extensionPrompt.title'))

    const description = computed(() => {
      // The Safari "store" is the Mac App Store listing of Linkflare for Mac,
      // so say why a Mac app is on the other end of the button.
      if (!bookmarkletOnly && browser === 'safari') return t('extensionPrompt.safariMacAppDescription')
      if (!bookmarkletOnly) return t('extensionPrompt.description')
      if (runsFromDock) return t('extensionPrompt.safariDescription')
      return `${t('extensionPrompt.safariDescription')} ${t('extensionPrompt.safariDockHint')}`
    })

    // The bookmarklet docs follow the banner's language; store pages have no
    // localized twin to pick.
    const ctaUrl = computed(() => bookmarkletOnly ? bookmarkletUrlFor(locale.value) : storeUrlFor(browser))

    const ctaLabel = computed(() => {
      if (bookmarkletOnly) return t('extensionPrompt.getBookmarklet')
      if (browser === 'firefox') return t('extensionPrompt.addToFirefox')
      if (browser === 'edge') return t('extensionPrompt.addToEdge')
      if (browser === 'safari') return t('extensionPrompt.addToSafari')
      return t('extensionPrompt.addToChrome')
    })

    const visible = computed(() => !extensionInstalled.value && !dismissed.value && !isMacApp && canInstall)

    const dismiss = () => {
      dismissed.value = true
      try {
        localStorage.setItem(dismissedKey, '1')
      } catch (e) {
        // Session-only dismissal is an acceptable degradation.
      }
    }

    const onInstallClick = () => {
      import('../services/analytics.js')
        .then(m => m.trackEvent('extension_prompt_click', { browser, target: bookmarkletOnly ? 'bookmarklet' : 'store' }))
        .catch(() => { })
    }

    return { visible, title, description, ctaUrl, ctaLabel, dismiss, onInstallClick }
  }
}
</script>

<style scoped>
.extension-prompt {
  margin: 0 auto 0.75rem;
  max-width: 1400px;
  padding: 0 1rem;
}

.prompt-content {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--bg-secondary);
}

.prompt-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--accent-light);
  color: var(--accent-color);
}

.prompt-text {
  flex: 1;
  min-width: 0;
}

.prompt-text h4 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
}

.prompt-text p {
  margin: 0.125rem 0 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.prompt-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.prompt-cta {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  background: var(--accent-color);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.prompt-cta:hover {
  background: color-mix(in srgb, var(--accent-color) 85%, black);
}

@media (max-width: 640px) {
  .prompt-content {
    flex-wrap: wrap;
  }

  .prompt-text {
    flex-basis: 100%;
    order: 1;
  }

  .prompt-actions {
    order: 2;
    margin-left: auto;
  }
}
</style>
